/**
 * The god-tool bar — Aether's only sink, per docs/01.
 *
 * Deliberately built on the same slot/tooltip grammar as the build hotbar: an
 * icon, a key hint, and everything else one hover away. The two bars do different
 * things but they are the same *kind* of thing — arm a tool, click a hex — and
 * teaching that grammar twice would be worse than reusing it. It shares the
 * `hb__` classes for exactly that reason.
 *
 * Kept as its own module rather than a second mode of `BuildHotbar` because the
 * affordability rule differs in a way that would riddle that class with
 * conditionals: buildings are priced in four resources and gated by terrain, god
 * tools are priced in Aether alone and gated by what is happening on the hex.
 */

import type { GodTool, ResourceBundle } from "kngdm-shared";
import { GOD_TOOLS, GOD_TOOLS_DEFINITIONS, canAfford } from "kngdm-shared";

export interface GodToolBarCallbacks {
  onSelect(tool: GodTool | null): void;
}

interface Slot {
  readonly tool: GodTool;
  readonly button: HTMLButtonElement;
  readonly costChip: HTMLElement;
  readonly onClick: () => void;
}

/** Hotkeys, matching `GOD_TOOL_KEYS` in the input controller. */
const KEYS: Record<GodTool, string> = {
  terraform: "Q",
  fortify: "W",
  calm: "E",
  provoke: "R",
};

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * One path per tool, in a 24x24 box.
 *
 * Keyed by `GodTool`, so a fifth tool will not compile until it has an icon —
 * a bar of identical blank squares is worse than a build error.
 */
const ICON_PATHS: Record<GodTool, string> = {
  // Sprout breaking through a furrow: ground becoming fertile.
  terraform: "M3 19h18v2H3zM12 17c0-4 2-7 6-8-1 4-2 6-6 8zM12 17c0-4-2-7-6-8 1 4 2 6 6 8zM11 11h2v6h-2z",
  // Shield.
  fortify: "M12 2l8 3.2v6c0 5-3.4 9.3-8 10.8-4.6-1.5-8-5.8-8-10.8v-6z",
  // Calm water: settling ripples.
  calm: "M3 8c3-2 6-2 9 0s6 2 9 0v2.6c-3 2-6 2-9 0s-6-2-9 0zM3 14c3-2 6-2 9 0s6 2 9 0v2.6c-3 2-6 2-9 0s-6-2-9 0z",
  // Storm bolt.
  provoke: "M13 2L4 14h6l-1 8 9-12h-6z",
};

export class GodToolBar {
  readonly element: HTMLElement;

  private readonly callbacks: GodToolBarCallbacks;
  private readonly slots: Slot[] = [];
  private active: GodTool | null = null;

  constructor(callbacks: GodToolBarCallbacks) {
    this.callbacks = callbacks;

    this.element = el("section", "panel panel--godtools");
    const list = el("ol", "hb");

    for (const tool of GOD_TOOLS) {
      const item = el("li", "hb__item");
      item.append(this.buildSlot(tool));
      list.append(item);
    }

    this.element.append(list);
  }

  /**
   * Re-evaluates affordability against banked Aether. Cheap enough per tick.
   *
   * Only cost is checked here. Whether there is an event to calm, or whether the
   * hex is already fortified, depends on *which hex* — which is not known until
   * the click, and is the server's answer regardless.
   */
  update(resources: ResourceBundle): void {
    for (const slot of this.slots) {
      const affordable = canAfford(resources, GOD_TOOLS_DEFINITIONS[slot.tool].cost);
      slot.button.classList.toggle("is-unaffordable", !affordable);
      slot.costChip.classList.toggle("is-missing", !affordable);
    }
  }

  setActive(tool: GodTool | null): void {
    if (tool === this.active) return;
    this.active = tool;
    for (const slot of this.slots) {
      slot.button.classList.toggle("is-active", slot.tool === tool);
      slot.button.setAttribute("aria-pressed", slot.tool === tool ? "true" : "false");
    }
  }

  destroy(): void {
    for (const slot of this.slots) slot.button.removeEventListener("click", slot.onClick);
    this.slots.length = 0;
    this.element.remove();
  }

  private select(tool: GodTool, button: HTMLButtonElement): void {
    // Focus would otherwise stay on the button, where a later space keypress —
    // the camera's pan modifier — would re-activate it.
    button.blur();
    const next = tool === this.active ? null : tool;
    this.setActive(next);
    this.callbacks.onSelect(next);
  }

  private buildSlot(tool: GodTool): HTMLElement {
    const definition = GOD_TOOLS_DEFINITIONS[tool];

    const button = el("button", "hb__slot");
    button.type = "button";
    button.setAttribute("aria-pressed", "false");
    button.setAttribute("aria-label", `${definition.label}. ${definition.description} Key ${KEYS[tool]}.`);
    button.append(iconFor(tool), el("span", "hb__key", KEYS[tool]));

    const tip = el("span", "hb__tip");
    tip.setAttribute("role", "tooltip");
    tip.append(
      el("span", "hb__tipname", definition.label),
      el("span", "hb__tipterrain", definition.description),
    );

    const costRow = el("span", "hb__cost");
    const costChip = el("span", "hb__chip", `${definition.cost.aether}Æ`);
    costRow.append(costChip);
    tip.append(costRow);
    tip.append(el("span", "hb__tiphint", `Press ${KEYS[tool]}, then click a hex`));

    const wrapper = el("span", "hb__slotwrap");
    wrapper.append(button, tip);

    const onClick = (): void => this.select(tool, button);
    button.addEventListener("click", onClick);
    this.slots.push({ tool, button, costChip, onClick });
    return wrapper;
  }
}

function iconFor(tool: GodTool): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.classList.add("hb__icon");

  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", ICON_PATHS[tool]);
  path.setAttribute("fill", "currentColor");
  svg.append(path);
  return svg;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
